import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, LayoutDashboard, ClipboardList, ClipboardCheck, CalendarDays, Search, GraduationCap, Users, UserCircle, BarChart3, PhoneOutgoing, Menu, X, BookOpen, Award, UserPlus, Wallet, Inbox, Brain, Activity, Settings, Database, DatabaseBackup, PhoneCall, KeyRound, Loader2, AlertTriangle, RotateCcw, Library, PartyPopper, CalendarHeart, Info, BrainCircuit, MessageCircleQuestion, UserCog, PanelLeftClose, PanelLeftOpen, FileSpreadsheet, UploadCloud, Wrench, Languages, Megaphone, Vote, ScanFace, Radio, ChevronDown } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import HeaderClock, { MobileClock } from "@/components/HeaderClock";
import ThemeToggle from "@/components/ThemeToggle";
import { SimulateButton, SimulationBanner } from "@/components/SimulationControls";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import PresenceDot from "@/components/PresenceDot";
import useIdleStatus from "@/lib/useIdleStatus";
import LiveClassBanner from "@/components/LiveClassBanner";
import UpdatesTicker from "@/components/UpdatesTicker";
import NotificationBell from "@/components/NotificationBell";

const jobPct = (job) => {
  if (!job) return 0;
  switch (job.stage) {
    case "queued": return 5;
    case "extracting": return 12;
    case "embedding": {
      const t = job.chunks_total || 0, d = job.chunks_done || 0;
      return t ? Math.min(90, 15 + Math.round((d / t) * 70)) : 20;
    }
    case "storing": return 95;
    default: return 8;
  }
};
const jobStageLabel = (job) => ({
  queued: "Queued", extracting: "Reading PDF", embedding: "Indexing text", storing: "Saving",
}[job?.stage] || "Indexing");

const baseLinks = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/attendance", label: "Attendance", icon: ClipboardList, testid: "nav-attendance" },
  { to: "/assessments", label: "Assessments", icon: Award, testid: "nav-assessments" },
  { to: "/timetable", label: "Timetable", icon: CalendarDays, testid: "nav-timetable" },
  { to: "/students", label: "Students", icon: Search, testid: "nav-students" },
  { to: "/homework", label: "Homework", icon: BookOpen, testid: "nav-homework" },
  { to: "/teacher/guidance", label: "Teacher Guidance", icon: Brain, testid: "nav-teacher-guidance" },
  { to: "/teacher/languages", label: "Language Learning", icon: Languages, testid: "nav-language-learning" },
  { to: "/polls", label: "Polls & Surveys", icon: Vote, testid: "nav-polls" },
  { to: "/more-info", label: "More Info", icon: Info, testid: "nav-more-info", badge: "requests" },
];

// Admin-only sidebar (custom order requested by user, excludes teacher-only screens)
const adminLinks = ({ isReadOnlySuper }) => [
  { to: "/admin/dashboard", label: "Admin overview", icon: LayoutDashboard, testid: "nav-admin-dashboard" },
  { to: "/admin/teachers", label: "Teachers", icon: Users, testid: "nav-admin-teachers" },
  { to: "/students", label: "Students", icon: GraduationCap, testid: "nav-students" },
  { to: "/admin/attendance", label: "Attendance", icon: ClipboardList, testid: "nav-admin-attendance" },
  { to: "/admin/face-attendance", label: "AI Attendance", icon: ScanFace, testid: "nav-admin-face-attendance", parent: "/admin/attendance" },
  { to: "/admin/live-classroom", label: "Live Classroom", icon: Radio, testid: "nav-admin-live-classroom" },
  { to: "/admin/results", label: "Results", icon: Award, testid: "nav-admin-results" },
  { to: "/announcements", label: "Announcements", icon: Megaphone, testid: "nav-announcements" },
  { to: "/polls", label: "Polls & Surveys", icon: Vote, testid: "nav-polls" },
  { to: "/timetable", label: "Timetable", icon: CalendarDays, testid: "nav-timetable" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/requests", label: "Requests", icon: Inbox, testid: "nav-requests", badge: "requests" },
  { to: "/teacher/guidance", label: "Teacher Guidance", icon: Brain, testid: "nav-teacher-guidance" },
  { to: "/teacher/languages", label: "Language Learning", icon: Languages, testid: "nav-language-learning" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "nav-admin-settings", badge: "settings" },
  { to: "/profile", label: "My Profile", icon: UserCircle, testid: "nav-profile" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop/tablet sidebar collapse — defaults to collapsed (icon rail) and
  // always collapses on tablet-width screens for more content space.
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) return true;
    const stored = localStorage.getItem("sidebar_collapsed");
    return stored !== null ? stored === "true" : true;
  });
  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", String(isCollapsed));
  }, [isCollapsed]);
  const [requestsCount, setRequestsCount] = useState(0);
  const [unassignedSections, setUnassignedSections] = useState(0);
  const [indexingCount, setIndexingCount] = useState(0);
  const [indexingJobs, setIndexingJobs] = useState([]);
  const [failedJobs, setFailedJobs] = useState([]);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isReadOnlySuper = user?.role === "super_admin";

  // Presence: flip to "away" after 5 min idle / tab hidden, and heartbeat the
  // backend every 60s so admins can see who's online + when they were last here.
  const presenceStatus = useIdleStatus();
  const statusRef = useRef(presenceStatus);
  statusRef.current = presenceStatus;
  useEffect(() => {
    if (!user?.id) return undefined;
    const ping = async () => {
      try {
        await api.post("/teacher/heartbeat", { active: statusRef.current === "online" });
      } catch { /* silent */ }
    };
    ping();
    const id = setInterval(ping, 60_000);
    const onVisibility = () => ping();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisibility); };
  }, [user?.id]);

  const isClassTeacher = (user?.class_teacher_of || []).length > 0;
  // Shallow-copy each entry so we never mutate the shared module-level baseLinks.
  let teacherLinks = baseLinks.map((l) => ({ ...l }));
  if (isClassTeacher) {
    const i = teacherLinks.findIndex((l) => l.to === "/assessments");
    teacherLinks.splice(i < 0 ? teacherLinks.length : i + 1, 0,
      { to: "/class-results", label: "Class Results", icon: Award, testid: "nav-class-results" },
      { to: "/teacher/class-tests", label: "Class Tests", icon: ClipboardCheck, testid: "nav-class-tests" });
    // Class teachers resolve student password-reset requests — give them a
    // direct sidebar item (with live count badge) instead of burying it in More Info.
    const s = teacherLinks.findIndex((l) => l.to === "/students");
    teacherLinks.splice(s < 0 ? teacherLinks.length : s + 1, 0,
      { to: "/requests", label: "Requests", icon: Inbox, testid: "nav-requests", badge: "requests" });
    // Drop the duplicate "requests" badge from More Info now that Requests is its own item.
    teacherLinks = teacherLinks.map((l) => (l.to === "/more-info" ? { ...l, badge: undefined } : l));
  }
  const links = isAdmin
    ? adminLinks({ isReadOnlySuper })
    : [...teacherLinks, { to: "/profile", label: "My Profile", icon: UserCircle, testid: "nav-profile" }];
  // Dev Admin gets a clean tools-only console; day-to-day admin/teacher pages
  // stay reachable by URL (and via Simulate) but are hidden from the nav.
  const devAdminLinks = [
    { to: "/devadmin/users", label: "Manage Users", icon: UserCog, testid: "nav-devadmin-users" },
    { to: "/devadmin/integrations", label: "Integrations", icon: Activity, testid: "nav-devadmin-integrations" },
    { to: "/devadmin/db", label: "Database", icon: Database, testid: "nav-devadmin-db" },
    { to: "/devadmin/backups", label: "Backups", icon: DatabaseBackup, testid: "nav-devadmin-backups" },
    { to: "/voice-calls", label: "Voice Call Log", icon: PhoneOutgoing, testid: "nav-devadmin-voice-calls" },
    { to: "/devadmin/config", label: "Settings", icon: KeyRound, testid: "nav-devadmin-config" },
    { to: "/devadmin/ai-tutor", label: "AI Tutor", icon: BrainCircuit, testid: "nav-devadmin-ai-tutor" },
    { to: "/admin/tuition-library", label: "Tuition Library", icon: Library, testid: "nav-devadmin-tuition-library" },
    { to: "/profile", label: "My Profile", icon: UserCircle, testid: "nav-profile" },
  ];
  // UtilityAdmin gets a focused nav: its Question Types page + the admin
  // config modules it can manage (Sections, Subjects, Assessments, Imports,
  // Tuition Library, Holidays, Events, Sessions & Devices — no Calls).
  // Feb 2026: promoted to functional-peer of Admin — same nav for the core
  // oversight areas (Admin overview, Teachers, Students, Attendance + AI
  // Attendance, Polls, Analytics) with full write access on the backend.
  const utilityAdminLinks = [
    { to: "/admin/dashboard", label: "Admin overview", icon: LayoutDashboard, testid: "nav-admin-dashboard" },
    { to: "/admin/teachers", label: "Teachers", icon: Users, testid: "nav-admin-teachers" },
    { to: "/students", label: "Students", icon: GraduationCap, testid: "nav-students" },
    { to: "/admin/attendance", label: "Attendance", icon: ClipboardList, testid: "nav-admin-attendance" },
    { to: "/admin/face-attendance", label: "AI Attendance", icon: ScanFace, testid: "nav-admin-face-attendance", parent: "/admin/attendance" },
    { to: "/polls", label: "Polls & Surveys", icon: Vote, testid: "nav-polls" },
    { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
    { to: "/utility/settings", label: "Configuration", icon: Settings, testid: "nav-utility-settings" },
    { to: "/utility/tools", label: "Utility", icon: Wrench, testid: "nav-utility-tools" },
    { to: "/utility/sats", label: "SATS", icon: Database, testid: "nav-utility-sats" },
    { to: "/utility/imports", label: "Imports", icon: UploadCloud, testid: "nav-utility-imports" },
    { to: "/utility/holidays", label: "Holidays", icon: PartyPopper, testid: "nav-utility-holidays" },
    { to: "/utility/events", label: "Events", icon: CalendarHeart, testid: "nav-utility-events" },
    { to: "/profile", label: "My Profile", icon: UserCircle, testid: "nav-profile" },
  ];
  const navLinks = user?.is_dev_admin
    ? devAdminLinks
    : user?.role === "utility_admin"
      ? utilityAdminLinks
      : links;

  // Poll the requests count every 60s — small payload, gives the sidebar a
  // live badge without a websocket.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetch = async () => {
      try {
        const { data } = await api.get("/requests/count");
        if (!cancelled) setRequestsCount(data.count || 0);
      } catch { /* silent */ }
    };
    fetch();
    const id = setInterval(fetch, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // Admins see a Settings badge nudging them to assign class teachers.
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const fetch = async () => {
      try {
        const { data } = await api.get("/admin/sections/unassigned-count");
        if (!cancelled) setUnassignedSections(data.count || 0);
      } catch { /* silent */ }
    };
    fetch();
    const id = setInterval(fetch, 120_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin]);

  // Admins get a live "Indexing…" pill while Tuition Library PDFs are being
  // processed in the background — visible from any admin screen.
  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/tuition/library/jobs/active/list");
      setIndexingJobs(data.jobs || []);
      setIndexingCount(data.count || 0);
      setFailedJobs(data.failed || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    if (!isAdmin) return undefined;
    loadJobs();
    const id = setInterval(loadJobs, 8_000);
    return () => clearInterval(id);
  }, [isAdmin, loadJobs]);

  const retryJob = async (jobId) => {
    try {
      await api.post(`/tuition/library/jobs/${jobId}/retry`);
      toast.success("Retrying — indexing restarted.");
      loadJobs();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not retry"); }
  };
  const dismissJob = async (jobId) => {
    try {
      await api.delete(`/tuition/library/jobs/${jobId}`);
      loadJobs();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not dismiss"); }
  };

  const badges = { requests: requestsCount, settings: unassignedSections };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const photoUrl = user?.has_photo ? `${apiBase}/profile/photo/${user.id}` : null;
  const desktopPad = isCollapsed ? "md:pl-16" : "md:pl-64";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <SimulationBanner />
      <SimulateButton />
      <Sidebar
        links={navLinks}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed((v) => !v)}
        role={user?.role}
        isDevAdmin={user?.is_dev_admin}
        onLogout={handleLogout}
        userName={user?.name}
        badges={badges}
      />

      {/* Top bar */}
      <header className={`${desktopPad} transition-[padding] duration-200 sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800`}>
        <div className="h-16 px-4 sm:px-6 lg:px-8 flex items-center gap-3">
          {/* Mobile menu trigger */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden h-9 w-9 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center"
            data-testid="open-sidebar-btn"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Left: live clock */}
          <div className="hidden md:flex items-center" data-testid="header-clock-slot">
            <HeaderClock />
          </div>

          {/* Live "Indexing…" pill — shows while Tuition Library PDFs process or failed */}
          {isAdmin && (indexingCount > 0 || failedJobs.length > 0) && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 h-8 pl-2 pr-3 rounded-full border text-xs font-medium transition-colors ${
                    indexingCount > 0
                      ? "bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800 hover:bg-sky-100 dark:hover:bg-sky-900/60"
                      : "bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800 hover:bg-rose-100 dark:hover:bg-rose-900/60"
                  }`}
                  data-testid="indexing-pill"
                  title="Tuition Library background indexing"
                >
                  {indexingCount > 0
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Indexing {indexingCount}{failedJobs.length > 0 ? ` · ${failedJobs.length} failed` : ""}</>
                    : <><AlertTriangle className="h-3.5 w-3.5" /> {failedJobs.length} failed</>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-96 p-0" data-testid="indexing-popover">
                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Tuition Library indexing</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {indexingCount} processing{failedJobs.length > 0 ? ` · ${failedJobs.length} failed` : ""}
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                  {indexingJobs.map((j) => (
                    <div key={j.id} className="px-4 py-2.5" data-testid={`indexing-job-${j.lesson_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate">{j.lesson_title || "Lesson"}</div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{j.subject_name} · {jobStageLabel(j)}</div>
                        </div>
                        <span className="text-[11px] font-semibold text-sky-600 dark:text-sky-400 shrink-0">{jobPct(j)}%</span>
                      </div>
                      <Progress value={jobPct(j)} className="h-1 mt-1.5" />
                    </div>
                  ))}
                  {failedJobs.map((j) => (
                    <div key={j.id} className="px-4 py-2.5 bg-rose-50/40 dark:bg-rose-950/20" data-testid={`failed-job-${j.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3 text-rose-500 shrink-0" /> {j.lesson_title || "Lesson"}
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{j.subject_name}</div>
                          {j.error && <div className="text-[11px] text-rose-600 dark:text-rose-400 mt-0.5 line-clamp-2">{j.error}</div>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button" onClick={() => retryJob(j.id)}
                            className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-[11px] font-medium"
                            data-testid={`retry-job-${j.id}`}
                          >
                            <RotateCcw className="h-3 w-3" /> Retry
                          </button>
                          <button
                            type="button" onClick={() => dismissJob(j.id)}
                            className="h-6 w-6 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-900/40 flex items-center justify-center"
                            title="Dismiss" data-testid={`dismiss-job-${j.id}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/admin/tuition-library")}
                  className="w-full px-4 py-2.5 text-xs font-medium text-sky-700 dark:text-sky-300 hover:bg-slate-50 dark:hover:bg-slate-800 border-t border-slate-100 dark:border-slate-800 text-left"
                  data-testid="indexing-open-library"
                >
                  Open Tuition Library →
                </button>
              </PopoverContent>
            </Popover>
          )}

          {/* Spacer pushes the user controls right */}
          <div className="flex-1" />

          {/* Right: theme, user, profile photo */}
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <NotificationBell />
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-sm font-medium text-slate-900 dark:text-slate-100" data-testid="header-user-name">{user?.name}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {user?.role === "super_admin"
                  ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 text-[10px] uppercase tracking-[0.15em]" data-testid="super-admin-badge">Read-only · Super</span>
                  : ((Array.isArray(user?.subjects) && user.subjects.length ? user.subjects.join(", ") : user?.subject) || (user?.role === "admin" ? "Administrator" : "Teacher"))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/profile")}
              className="relative h-10 w-10 rounded-full overflow-visible bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center font-semibold ring-2 ring-transparent hover:ring-sky-200 dark:hover:ring-sky-700 transition"
              data-testid="header-profile-pic"
              aria-label="My profile"
            >
              <span className="h-full w-full rounded-full overflow-hidden flex items-center justify-center">
                {photoUrl ? (
                  <img src={photoUrl} alt={user?.name} className="h-full w-full object-cover" />
                ) : (
                  <span>{user?.name?.split(" ").map((p) => p[0]).slice(0, 2).join("")}</span>
                )}
              </span>
              {/* Presence dot — auto-flips to "away" after 5 min idle / tab hidden. */}
              <span
                className="absolute bottom-0 right-0 inline-flex p-0.5 rounded-full bg-white dark:bg-slate-900 shadow"
                title={presenceStatus === "online" ? "You are online" : "You are away (idle)"}
                data-testid="header-presence-dot"
              >
                <PresenceDot status={presenceStatus} size="sm" />
              </span>
            </button>
          </div>
        </div>
        {/* Mobile-only clock strip (md+ shows the clock inline on the left) */}
        <div className="md:hidden flex items-center justify-center pb-2 pt-0">
          <MobileClock />
        </div>
      </header>

      <UpdatesTicker endpoint="/updates/ticker" />

      <main className={`${desktopPad} transition-[padding] duration-200`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LiveClassBanner variant="staff" displayName={user?.name} />
          {children}
        </div>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function Sidebar({ links, mobileOpen, onClose, isCollapsed, onToggleCollapse, role, isDevAdmin, onLogout, userName, badges = {} }) {
  const location = useLocation();
  // Active path including the ?next= query so /select?next=/attendance and
  // /select?next=/timetable don't both light up at the same time.
  const currentFull = `${location.pathname}${location.search}`;
  // Collapsed styling only applies on md+ screens — the mobile drawer always
  // shows full labels for readability.
  const cx = (collapsedCls) => (isCollapsed ? collapsedCls : "");
  // Collapsible parent groups (persisted so the user's choice sticks).
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem("sidebar_expanded_groups");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem("sidebar_expanded_groups", JSON.stringify([...expandedGroups])); }
    catch { /* silent */ }
  }, [expandedGroups]);
  const toggleGroup = (to) => {
    setExpandedGroups((prev) => {
      const n = new Set(prev);
      if (n.has(to)) n.delete(to); else n.add(to);
      return n;
    });
  };
  return (
    <TooltipProvider delayDuration={0}>
      {/* Backdrop for mobile */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm"
          onClick={onClose}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 ${cx("md:w-16")} bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transform transition-[transform,width] duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
        data-testid="sidebar"
        data-collapsed={isCollapsed}
      >
        <div className={`h-16 px-5 ${cx("md:px-3")} flex items-center justify-between border-b border-slate-200 dark:border-slate-800`}>
          <div className={`flex items-center gap-2.5 min-w-0 ${cx("md:gap-0 md:justify-center md:w-full")}`}>
            <img src="/trishul-logo.png" alt="Trishul High School" className="h-9 w-9 rounded-full shrink-0" />
            <div className={`leading-tight min-w-0 ${cx("md:hidden")}`}>
              <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate" style={{fontFamily:"Outfit"}} data-testid="sidebar-school-name">Trishul High School</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 truncate">
                {isDevAdmin ? "Dev console" : role === "super_admin" ? "Oversight · read-only" : role === "admin" ? "Admin console" : role === "utility_admin" ? "Utility console" : "Teacher console"}
              </div>
            </div>
          </div>
          {/* Desktop/tablet collapse toggle */}
          <button
            type="button"
            onClick={onToggleCollapse}
            className={`hidden md:flex h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 items-center justify-center shrink-0 ${cx("md:absolute md:top-4 md:-right-3 md:h-6 md:w-6 md:rounded-full md:border md:border-slate-200 md:dark:border-slate-700 md:bg-white md:dark:bg-slate-900 md:shadow")}`}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="collapse-sidebar-btn"
          >
            {isCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
          {/* Mobile close */}
          <button
            type="button"
            onClick={onClose}
            className="md:hidden h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center"
            aria-label="Close menu"
            data-testid="close-sidebar-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className={`px-3 ${cx("md:px-2")} py-4 space-y-1 overflow-y-auto`} style={{maxHeight: "calc(100vh - 8rem)"}}>
          {(() => {
            // Compute which parents have an active child in the URL — those
            // groups auto-open regardless of stored preference.
            const activeParent = new Set();
            for (const c of links) {
              if (!c.parent) continue;
              const hit = location.pathname === c.to || (c.to !== "/" && location.pathname.startsWith(c.to + "/"));
              if (hit) activeParent.add(c.parent);
            }
            return links.map((l) => {
              const hasQuery = l.to.includes("?");
              const isActive = hasQuery
                ? currentFull === l.to
                : location.pathname === l.to || (l.to !== "/" && location.pathname.startsWith(l.to + "/"));
              const hasBadge = l.badge && badges[l.badge] > 0;
              const isChild = !!l.parent;
              const isParent = links.some((c) => c.parent === l.to);
              const isOpen = isParent && (isCollapsed || expandedGroups.has(l.to) || activeParent.has(l.to) || isActive);
              // Hide children when their parent is collapsed (icon rail always shows all).
              if (isChild && !isCollapsed && !(expandedGroups.has(l.parent) || activeParent.has(l.parent) || isActive)) {
                return null;
              }
              const link = (
                <NavLink
                  key={`${l.to}-${l.label}`}
                  to={l.to}
                  onClick={onClose}
                  data-testid={l.testid}
                  end={hasQuery}
                  className={`relative flex items-center gap-3 rounded-xl transition-colors ${
                    isChild
                      ? `pl-9 pr-3 py-2 text-[13px] ${cx("md:justify-center md:pl-0 md:pr-0")}`
                      : `px-3 py-2.5 text-sm font-medium ${cx("md:justify-center md:px-0")}`
                  } ${
                    isActive
                      ? "bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300"
                      : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  {/* Sub-item marker (only visible when the sidebar is expanded). */}
                  {isChild && (
                    <span aria-hidden="true"
                      className={`absolute left-4 top-1/2 -translate-y-1/2 h-3 w-3 border-l border-b border-slate-300 dark:border-slate-600 rounded-bl ${cx("md:hidden")}`} />
                  )}
                  <l.icon className={`${isChild ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0`} />
                  <span className={`flex-1 ${cx("md:hidden")}`}>{l.label}</span>
                  {/* Chevron toggle for parent groups — only visible when the sidebar is expanded. */}
                  {isParent && !isCollapsed && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGroup(l.to); }}
                      aria-label={isOpen ? "Collapse group" : "Expand group"}
                      className={`ml-auto -mr-1 p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 ${cx("md:hidden")}`}
                      data-testid={`nav-toggle-${l.testid || l.to.replace(/\W/g, "-")}`}
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                    </button>
                  )}
                  {hasBadge && (
                    <>
                      {/* Full count when expanded */}
                      <span data-testid={`badge-${l.badge}`} className={`${isParent && !isCollapsed ? "" : "ml-auto"} inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none ${cx("md:hidden")}`}>
                        {badges[l.badge] > 99 ? "99+" : badges[l.badge]}
                      </span>
                      {/* Dot indicator when collapsed */}
                      {isCollapsed && (
                        <span className="hidden md:block absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900" />
                      )}
                    </>
                  )}
                </NavLink>
              );
              // In collapsed mode, wrap each item in a hover tooltip revealing its label.
              if (isCollapsed) {
                return (
                  <Tooltip key={`${l.to}-${l.label}`}>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right" className="hidden md:block">{l.label}</TooltipContent>
                  </Tooltip>
                );
              }
              return link;
            });
          })()}
        </nav>

        {/* Logout pinned at the bottom of the sidebar */}
        <div className={`absolute bottom-0 inset-x-0 px-3 ${cx("md:px-2")} py-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900`}>
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onLogout}
                  data-testid="logout-btn"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors md:justify-center md:px-0"
                  title={`Sign out ${userName || ""}`}
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  <span className="md:hidden">Sign out</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="hidden md:block">Sign out</TooltipContent>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={onLogout}
              data-testid="logout-btn"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
              title={`Sign out ${userName || ""}`}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span>Sign out</span>
            </button>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
